import type { DebugCostByRound, DebugStep } from './types';

interface ReviewerOutputPanelProps {
  totalCostUsd: number;
  costByRound: DebugCostByRound[];
  steps: DebugStep[];
}

function formatCost(usd: number): string {
  return `$${usd.toFixed(4)}`;
}

function aggregateReviewerTotals(steps: DebugStep[]): Array<{
  reviewerId: string;
  model: string;
  totalCost: number;
  totalDuration: number;
  totalFindings: number;
  inputTokens: number;
  outputTokens: number;
}> {
  const byReviewer: Record<string, {
    reviewerId: string; model: string;
    totalCost: number; totalDuration: number; totalFindings: number;
    inputTokens: number; outputTokens: number;
  }> = {};
  for (const step of steps) {
    if (!step.reviewerResults) continue;
    for (const r of step.reviewerResults) {
      const existing = byReviewer[r.reviewerId];
      if (existing) {
        // `?? 0` — one unpriced reviewer must not turn the whole aggregate
        // into NaN. Matches how the backend sums (spec 261 Phase 3b): an
        // unpriced call contributes nothing to a total, and the gap is
        // reported by model_pricing_unknown rather than by a broken number.
        existing.totalCost += r.costUsd ?? 0;
        existing.totalDuration += r.durationMs;
        existing.totalFindings += r.findingsCount;
        existing.inputTokens += r.inputTokens;
        existing.outputTokens += r.completionTokens;
      } else {
        byReviewer[r.reviewerId] = {
          reviewerId: r.reviewerId,
          model: r.model,
          totalCost: r.costUsd ?? 0,
          totalDuration: r.durationMs,
          totalFindings: r.findingsCount,
          inputTokens: r.inputTokens,
          outputTokens: r.completionTokens,
        };
      }
    }
  }
  return Object.values(byReviewer).sort((a, b) => b.totalCost - a.totalCost);
}

export function ReviewerOutputPanel({
  totalCostUsd,
  costByRound,
  steps,
}: ReviewerOutputPanelProps) {
  const reviewerTotals = aggregateReviewerTotals(steps);

  return (
    <aside data-testid="reviewer-output-panel" className="space-y-6">
      <section className="rounded-lg border border-gray-800 bg-gray-900 p-4">
        <h2 className="mb-2 text-sm font-semibold uppercase text-gray-400">
          Total cost
        </h2>
        <p data-testid="total-cost" className="font-mono text-2xl">
          ${totalCostUsd.toFixed(2)}
        </p>
      </section>

      <section className="rounded-lg border border-gray-800 bg-gray-900 p-4">
        <h2 className="mb-2 text-sm font-semibold uppercase text-gray-400">
          Cost by round
        </h2>
        {costByRound.length === 0 ? (
          <p className="text-sm text-gray-500">No rounds yet</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-gray-500">
              <tr>
                <th className="text-left py-1">Round</th>
                <th className="text-left py-1">Reviewer</th>
                <th className="text-right py-1">Cost</th>
              </tr>
            </thead>
            <tbody data-testid="cost-by-round">
              {costByRound.flatMap((round) =>
                Object.entries(round.reviewerBreakdown).map(([reviewer, cost]) => (
                  <tr key={`${round.round}-${reviewer}`} className="border-t border-gray-800">
                    <td className="py-1 font-mono">{round.round}</td>
                    <td className="py-1 text-teal-300">{reviewer}</td>
                    {/* A per-reviewer entry can be null (unpriced model);
                        rendering "unknown" keeps it distinct from a free call. */}
                    <td className="py-1 text-right font-mono">
                      {cost === null ? 'unknown' : formatCost(cost)}
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        )}
      </section>

      <section className="rounded-lg border border-gray-800 bg-gray-900 p-4">
        <h2 className="mb-2 text-sm font-semibold uppercase text-gray-400">
          Reviewer totals
        </h2>
        {reviewerTotals.length === 0 ? (
          <p className="text-sm text-gray-500">No reviewer results yet</p>
        ) : (
          <ul data-testid="reviewer-totals" className="space-y-2 text-xs">
            {reviewerTotals.map((r) => (
              <li key={r.reviewerId} className="border-t border-gray-800 pt-2 font-mono">
                <div className="flex justify-between">
                  <span className="text-teal-300">{r.reviewerId}</span>
                  <span className="text-gray-400">{r.model}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>in: {r.inputTokens}</span>
                  <span>out: {r.outputTokens}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>{r.totalFindings} findings</span>
                  <span>{formatCost(r.totalCost)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </aside>
  );
}
